/**
 * SPEC-20260507-0136 (Fase 3): catálogo de steps disponíveis no Composer.
 *
 * Lista steps do `GET /api/step-catalog` agrupados por tag. Usuário clica
 * pra adicionar ao painel central (drag-drop pode vir depois — clique
 * funciona pro MVP).
 */

import { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import type { StepCatalogEntry } from "../../types/saga-definition";

export interface StepCatalogSidebarProps {
  catalog: StepCatalogEntry[];
  onAdd(stepType: string): void;
}

export function StepCatalogSidebar({ catalog, onAdd }: StepCatalogSidebarProps) {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const lower = q.toLowerCase();
    return catalog.filter(
      (e) =>
        !q ||
        e.type.toLowerCase().includes(lower) ||
        e.description.toLowerCase().includes(lower) ||
        e.tags.some((t) => t.toLowerCase().includes(lower)),
    );
  }, [catalog, q]);

  // Agrupa por tag primária (1ª tag) — fallback "outros"
  const grouped = useMemo(() => {
    const groups: Record<string, StepCatalogEntry[]> = {};
    for (const e of filtered) {
      const tag = e.tags[0] ?? "outros";
      groups[tag] = groups[tag] ?? [];
      groups[tag].push(e);
    }
    return groups;
  }, [filtered]);

  return (
    <aside className="rounded-md border border-border bg-surface p-3">
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
        Catálogo de steps
      </h3>
      <div className="relative mb-3">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="filtrar..."
          className="w-full pl-7 pr-2 py-1.5 text-xs border border-border rounded bg-bg focus:outline-none focus:ring-1 focus:ring-primary/40"
        />
      </div>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        {Object.entries(grouped).map(([tag, entries]) => (
          <div key={tag}>
            <p className="text-[10px] uppercase text-muted-foreground font-medium mb-1.5">
              {tag} ({entries.length})
            </p>
            <ul className="space-y-1">
              {entries.map((e) => (
                <li key={e.type}>
                  <button
                    type="button"
                    onClick={() => onAdd(e.type)}
                    className="w-full text-left rounded border border-border-subtle bg-bg hover:bg-surface-subtle px-2 py-1.5 group"
                    title={e.description}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <code className="font-mono-tight text-xs truncate">{e.type}</code>
                      <Plus size={12} className="text-muted-foreground group-hover:text-foreground shrink-0" />
                    </div>
                    <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                      {e.description}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {Object.keys(grouped).length === 0 && (
          <p className="text-xs text-muted-foreground italic text-center py-4">Nenhum step bate.</p>
        )}
      </div>
    </aside>
  );
}
